import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Pencil, X, Search, IndianRupee, ChevronDown } from "lucide-react";
import { MachineMark } from "@/components/maintenance/MachineMark";
import { categoryLabel, formatINR } from "@/components/maintenance/lib";

// Admin corner — the per-machine ₹/hr downtime rate. Cost = downtime hours ×
// this rate, snapshotted onto a ticket when it closes so history never drifts
// (MAINTENANCE_MODULE_PLAN.md §7). Rates are optional by design: until one is
// set the machine shows hours only, which is a valid state, not an error.
//
// Inline-edit follows the locked RockLink vocabulary: the VALUE is the click
// target, a pencil slides out on local hover, and clearing is one tiny ×.

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function RateCell({ machine, onSave }) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState("");
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    if (!editing) return;
    setValue(machine.downtime_rate_inr == null ? "" : String(machine.downtime_rate_inr));
    const t = setTimeout(() => { inputRef.current?.focus(); inputRef.current?.select(); }, 30);
    return () => clearTimeout(t);
  }, [editing, machine.downtime_rate_inr]);

  const commit = async (raw) => {
    const text = String(raw ?? value).trim();
    const next = text === "" ? null : Number(text);
    if (next !== null && (!Number.isFinite(next) || next < 0)) {
      toast.error("Enter a number, or leave it blank to clear the rate.");
      return;
    }
    if (next === (machine.downtime_rate_inr ?? null)) { setEditing(false); return; }
    setBusy(true);
    try {
      await axios.put(`${API}/maintenance/machines/${machine.machine_no}/rate`,
        { downtime_rate_inr: next });
      onSave(machine.machine_no, next);
      setEditing(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save that rate.");
    } finally { setBusy(false); }
  };

  if (editing) {
    return (
      <span className="inline-flex items-center gap-1.5">
        <span className="text-[hsl(var(--muted-foreground))] text-[12px]">₹</span>
        <input
          ref={inputRef}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") { e.preventDefault(); commit(); }
            if (e.key === "Escape") { e.preventDefault(); setEditing(false); }
          }}
          onBlur={() => commit()}
          disabled={busy}
          inputMode="decimal"
          data-testid={`mt-rate-input-${machine.machine_no}`}
          className="w-[86px] h-7 rounded-md border border-[hsl(var(--primary))]/60 bg-[hsl(var(--card))] px-2 text-[12.5px] font-mono-data text-right outline-none"
        />
        <span className="text-[10.5px] text-[hsl(var(--muted-foreground))]">/hr</span>
      </span>
    );
  }

  const has = machine.downtime_rate_inr != null;
  return (
    <span className="group/rate inline-flex items-center">
      <button
        onClick={() => setEditing(true)}
        data-testid={`mt-rate-${machine.machine_no}`}
        title={has ? "Change this machine's downtime rate" : "Set a ₹/hr downtime rate"}
        className={`inline-flex items-center gap-1 cursor-pointer text-[12.5px] font-mono-data font-bold ${has ? "" : "text-[hsl(var(--muted-foreground))] font-normal"}`}
      >
        {has ? <>{formatINR(machine.downtime_rate_inr)}<span className="text-[10.5px] font-normal text-[hsl(var(--muted-foreground))] ml-0.5">/hr</span></> : "— set rate"}
        {/* Pencil slides out on LOCAL hover — collapsed at rest so the value
            keeps its own width (locked inline-edit vocabulary). */}
        <span className="max-w-0 overflow-hidden group-hover/rate:max-w-[18px] transition-[max-width] duration-200">
          <Pencil className="w-3 h-3 ml-1 text-[hsl(var(--muted-foreground))]" />
        </span>
      </button>
      {has && (
        <button
          onClick={() => commit("")}
          title="Clear this rate"
          aria-label="Clear rate"
          data-testid={`mt-rate-clear-${machine.machine_no}`}
          className="max-w-0 overflow-hidden group-hover/rate:max-w-[18px] transition-[max-width] duration-200 text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))]"
        >
          <X className="w-3 h-3 ml-1" />
        </button>
      )}
    </span>
  );
}

export default function MachineRatesPanel() {
  const [open, setOpen] = useState(false);
  const [machines, setMachines] = useState(null);
  const [query, setQuery] = useState("");

  useEffect(() => {
    if (!open || machines) return;
    axios.get(`${API}/maintenance/machines`)
      .then((r) => setMachines(r.data?.machines || []))
      .catch(() => toast.error("Could not load the machine registry."));
  }, [open, machines]);

  const onSave = useCallback((machine_no, rate) => {
    setMachines((ms) => (ms || []).map((m) => (m.machine_no === machine_no ? { ...m, downtime_rate_inr: rate } : m)));
    toast.success(rate == null ? "Rate cleared" : `Rate set to ${formatINR(rate)}/hr`);
  }, []);

  const rows = useMemo(() => {
    const q = query.trim().toLowerCase();
    return (machines || []).filter((m) => !q || `${m.name} ${m.machine_no} ${m.make}`.toLowerCase().includes(q));
  }, [machines, query]);

  const priced = (machines || []).filter((m) => m.downtime_rate_inr != null).length;

  return (
    <div className="rl-glass-edge rounded-[14px] bg-[hsl(var(--card))] mt-4 overflow-hidden" data-testid="mt-machine-rates">
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center gap-2 px-[18px] py-3.5 text-left"
        data-testid="mt-machine-rates-toggle"
      >
        <IndianRupee className="w-3.5 h-3.5 text-[hsl(var(--primary))]" />
        <span className="text-[11px] font-bold uppercase tracking-[0.08em] text-[hsl(var(--foreground))]/85">Machine downtime rates</span>
        <span className="text-[11px] text-[hsl(var(--muted-foreground))]">
          {machines ? `${priced} of ${machines.length} machines priced` : "admin only"}
        </span>
        <ChevronDown className={`w-4 h-4 ml-auto text-[hsl(var(--muted-foreground))] transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div className="border-t border-[hsl(var(--border))]">
          <div className="px-[18px] py-3 flex items-center gap-3 flex-wrap">
            <div className="rl-carved flex items-center gap-2 h-8 px-3.5 rounded-full bg-[hsl(var(--muted))]/50">
              <Search className="w-3.5 h-3.5 text-[hsl(var(--muted-foreground))]" />
              <input
                value={query} onChange={(e) => setQuery(e.target.value)}
                placeholder="Search machine…"
                data-testid="mt-rates-search"
                className="bg-transparent outline-none text-[12px] w-[170px] placeholder:text-[hsl(var(--muted-foreground))]"
              />
            </div>
            <p className="text-[11px] text-[hsl(var(--muted-foreground))] max-w-[520px] leading-relaxed">
              Downtime cost = hours the machine was <b>stopped</b> × this rate. The rate is copied onto a
              breakdown when it closes, so editing it here never changes past records.
            </p>
          </div>
          <div className="max-h-[420px] overflow-y-auto">
            <table className="w-full border-collapse">
              <thead className="rl-thead">
                <tr>
                  <th className="text-left px-4 py-2.5 text-[9.5px] uppercase tracking-[0.09em] font-semibold text-[hsl(var(--muted-foreground))]">Machine</th>
                  <th className="text-left px-4 py-2.5 text-[9.5px] uppercase tracking-[0.09em] font-semibold text-[hsl(var(--muted-foreground))]">Category</th>
                  <th className="text-left px-4 py-2.5 text-[9.5px] uppercase tracking-[0.09em] font-semibold text-[hsl(var(--muted-foreground))]">Shed</th>
                  <th className="text-right px-4 py-2.5 text-[9.5px] uppercase tracking-[0.09em] font-semibold text-[hsl(var(--muted-foreground))]">Downtime rate</th>
                </tr>
              </thead>
              <tbody>
                {machines === null && (
                  <tr><td colSpan={4} className="px-4 py-8 text-center text-[12px] text-[hsl(var(--muted-foreground))]">Loading…</td></tr>
                )}
                {rows.map((m) => (
                  <tr key={m.machine_no} className="hover:bg-[hsl(var(--muted))]/25">
                    <td className="px-4 py-2.5 border-t border-[hsl(var(--border))]/60">
                      <div className="flex items-center gap-2.5 min-w-0">
                        <MachineMark make={m.make} size={24} />
                        <span className="text-[13px] font-bold truncate">{m.name}</span>
                        <span className="rl-me-chip rl-carved shrink-0">{m.machine_no}</span>
                      </div>
                    </td>
                    <td className="px-4 py-2.5 border-t border-[hsl(var(--border))]/60 text-[11.5px] text-[hsl(var(--muted-foreground))]">{categoryLabel(m.category)}</td>
                    <td className="px-4 py-2.5 border-t border-[hsl(var(--border))]/60 text-[11.5px] text-[hsl(var(--muted-foreground))]">{m.area === "second_shed" ? "Second shed" : "Main shed"}</td>
                    <td className="px-4 py-2.5 border-t border-[hsl(var(--border))]/60 text-right"><RateCell machine={m} onSave={onSave} /></td>
                  </tr>
                ))}
                {machines && rows.length === 0 && (
                  <tr><td colSpan={4} className="px-4 py-8 text-center text-[12px] text-[hsl(var(--muted-foreground))]">No machine matches that search.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
